"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { siteConfig } from "@/app";
import { Link } from "@/navigation";
import { type SidebarNavItem, type MainMenuItem } from "@/types";
import { useTranslations } from "next-intl";
import { TheosIcon } from "@/components/icons";
import { cn } from "@/lib/utils";

import { Icons } from "@/components/icons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger,   SheetHeader,
  SheetTitle, } from "@/components/ui/sheet";
import { PrimaryButton } from "@/components/ui/primary-button";
import { capturePH } from "@/instrumentation-client";

interface MobileMenuProps {
  MainMenuItems?: MainMenuItem[];
  sidebarNavItems: SidebarNavItem[];
}

function MenuSvg({ className }: { className?: string }) {
  return (
    <svg className={cn("-mr-1.5 shrink-0", className)} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.99902 7.71436H19.999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      </path>
      <path d="M3.99902 16.2856H19.999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      </path>
    </svg>
  );
}

export function MobileMenu({
  MainMenuItems,
  sidebarNavItems,
}: MobileMenuProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = React.useState(false);
  const t = useTranslations("siteConfig");

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          className="px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden"
        >
          <MenuSvg className="!h-6 !w-6" />
          <span className="sr-only">Toggle Menu</span>
          {/* <span className="ml-2 font-heading tracking-wide">Relivator</span> */}
          {/* <Icons.logo className="ml-2 h-3" /> */}
          {/* <TheosIcon /> */}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="pl-1 pr-0">
        <SheetHeader>
          <SheetTitle>
          <div className="p-2">
          <Link
            aria-label="Home"
            href="/"
            className="flex items-center"
            onClick={() => setIsOpen(false)}
          >
            <Icons.logo className="h-3" />
          </Link>
          </div>
          </SheetTitle>
        </SheetHeader>
        {/* <div className="px-7">
          <Link
            aria-label="Home"
            href="/"
            className="flex items-center"
            onClick={() => setIsOpen(false)}
          >
            <Icons.logo className="h-3" />
          </Link>
        </div> */}
        <ScrollArea className="my-4 h-[calc(100vh-8rem)] pb-10 pl-6">
          <div className="pl-1 pr-7">
            <Accordion type="single" collapsible className="w-full">
              {MainMenuItems?.map((item, index) => {
                const hasChildren = Boolean(item.items && item.items.length > 0);
                if (!hasChildren) {
                  return item.href ? (
                    <div key={index} className="border-b last:border-b-0">
                      <MobileLink
                        className="focus-visible:border-ring focus-visible:ring-ring/50 flex w-full items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium capitalize transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50"
                        href={String(item.href)}
                        pathname={pathname}
                        setIsOpen={setIsOpen}
                        disabled={item.disabled}
                      >
                        {t(item.title)}
                      </MobileLink>
                    </div>
                  ) : (
                    <div key={index} className="text-foreground/70 transition-colors">
                      {t(item.title)}
                    </div>
                  );
                }
                return (
                  <AccordionItem value={item.title} key={index}>
                    <AccordionTrigger className="text-sm capitalize">
                      {t(item.title)}
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4">
                      <div className="flex flex-col space-y-4">
                        {item.items?.map(
                          (subItem, index) => (
                            <div
                              className="flex flex-col space-y-2"
                              key={subItem.title}
                            >
                              {
                                subItem.items.length > 0 ? (
                                  <div
                                    key={index}
                                    className="text-muted-foreground/70 transition-colors"
                                  >
                                    {t(subItem.title)}
                                  </div>)
                                  : <MobileLink
                                    key={index}
                                    href={String(subItem.href)}
                                    pathname={pathname}
                                    setIsOpen={setIsOpen}
                                    disabled={subItem.disabled}
                                  >
                                    {t(subItem.title)}
                                  </MobileLink>
                              }
                              {subItem.items?.map((item, index) =>
                                item.href ? (
                                  <MobileLink
                                    key={index}
                                    href={String(item.href)}
                                    pathname={pathname}
                                    setIsOpen={setIsOpen}
                                    disabled={item.disabled}
                                  >
                                    {t(item.title)}
                                  </MobileLink>
                                ) : (
                                  <div
                                    key={index}
                                    className="text-foreground/70 transition-colors"
                                  >
                                    {t(item.title)}
                                  </div>
                                )
                              )}
                            </div>
                          )
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
              {/* <AccordionItem value="sidebar">
                <AccordionTrigger className="text-sm">
                  Sidebar Menu
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col space-y-2">
                    {sidebarNavItems?.map((item, index) =>
                      item.href ? (
                        <MobileLink
                          key={index}
                          href={String(item.href)}
                          pathname={pathname}
                          setIsOpen={setIsOpen}
                          disabled={item.disabled}
                        >
                          {item.title}
                        </MobileLink>
                      ) : (
                        <div
                          key={index}
                          className="text-foreground/70 transition-colors"
                        >
                          {item.title}
                        </div>
                      ),
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem> */}
            </Accordion>
          </div>
        </ScrollArea>
        <div className="border-t px-6 py-5 space-y-3">
          <PrimaryButton
            className="w-full h-10"
            variant="black"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              try { capturePH('book_demo_clicked', { location: 'mobile_menu' }) } catch {}
              window.open('/demo', '_self');
            }}
          >
            {t('Get started')}
          </PrimaryButton>
          <PrimaryButton
            className="w-full h-10"
            variant="light"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              window.open('https://app.theos.ai', '_self');
            }}
          >
            {t('Login')}
          </PrimaryButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface MobileLinkProps {
  children?: React.ReactNode;
  href: string | null;
  disabled?: boolean;
  pathname: string | null;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
}

function MobileLink({
  children,
  href,
  disabled,
  pathname,
  setIsOpen,
  className,
}: MobileLinkProps) {
  return (
    <Link
      href={`${href}`}
      className={cn(
        className,
        "text-foreground transition-colors hover:text-foreground/70",
        pathname === href && "text-foreground",
        disabled && "pointer-events-none opacity-60"
      )}
      onClick={() => setIsOpen(false)}
    >
      {children}
    </Link>
  );
}
